import React, { useMemo } from 'react';
import { BarChart, Bar, Rectangle, CartesianGrid, XAxis } from 'recharts';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription
} from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import { formatDataSize } from '@/utils/format';
import { TooltipProps } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';

const chartConfig = {
	desktop: {
		label: "Desktop",
		color: '#C0E6FF'
	},
} satisfies ChartConfig;

interface StatsBarChartProps {
	title: string;
	data: Array<any>;
	xDataKey: string;
	yDataKey: string;
	description?: string;
	icon?: React.ReactNode;
	className?: string;
	descriptionStyleOverride?: string;
	isLoading?: boolean;
}

interface CustomTooltipProps extends TooltipProps<number, string> { }

const CustomTooltip: React.FC<CustomTooltipProps> = ({
	active,
	payload,
	label,
}) => {
	if (!active || !payload || payload.length === 0) {
		return null;
	}
	console.log(active, payload, label);

	const formattedLabel = `${formatDataSize(payload[0].payload['LOWER_BOUND'])} - ${formatDataSize(payload[0].payload['UPPER_BOUND'])}`
	const rawValue = Math.pow(10, (payload as any)[0].value).toFixed(0);

	return (
		<div className="p-2 bg-[#011829]/80 backdrop-blur-md text-white rounded shadow">
			<p className="mb-1 text-sm font-medium">{formattedLabel}</p>
			<p className="text-sm">
				Total Count: {Number(rawValue).toLocaleString()}
			</p>
		</div>
	);
};

export const LogStatsBarChart = (props: StatsBarChartProps) => {
	const { title, data, icon, xDataKey, yDataKey, className, description, descriptionStyleOverride, isLoading = false } = props;

	const transformedData = useMemo(() => {
		if (!data) return [];


		return data
			.filter((d) => d[yDataKey] > 0)
			.map(d => ({
				...d,
				logValue: Math.log10(d[yDataKey])
			}));
	}, [data]);

	return (
		<Card className={cn(
			'bg-[#0C0F1D]/70 backdrop-blur-md text-black border-2 border-black rounded-sm shadow-[4px_4px_0_0_black] p-4',
			'transition-colors duration-150',
			className
		)}>
			<CardHeader className="p-0 mb-2">
				<div className='flex flex-row items-center justify-between'>
					<div className="flex items-center gap-2">
						{icon && <div className="p-1 text-white">{icon}</div>}
						<CardTitle className="text-sm font-bold text-white">{title}</CardTitle>
					</div>
				</div>
				{description && (
					<CardDescription className={cn('text-xs mt-1 text-white', descriptionStyleOverride)}>
						{description}
					</CardDescription>
				)}
			</CardHeader>

			<CardContent className="p-0 pt-2">
				{isLoading ?
					<Skeleton className='h-45 w-auto bg-white/20' />
					:
					!!transformedData?.length && (
						<ChartContainer config={chartConfig}>
							<BarChart
								data={transformedData}
								margin={{ top: 12, bottom: 0 }}
							>
								<defs>
									<linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor="#A084DC" stopOpacity={0.8} />
										<stop offset="95%" stopColor="#A084DC" stopOpacity={0.1} />
									</linearGradient>
								</defs>
								<CartesianGrid stroke="#000000" strokeDasharray="3 3" vertical={false} />
								<XAxis
									dataKey={xDataKey}
									tickLine={false}
									axisLine={false}
									tick={false}
									padding={{ left: 20, right: 20 }}
								/>

								<ChartTooltip
									cursor={{ stroke: '#FFFFFF', strokeWidth: 2, strokeDasharray: '3 3' }}
									content={<CustomTooltip />}
								/>

								<Bar
									dataKey="logValue"
									fill="url(#gradient)"
									stroke="#000000"
									strokeWidth={1.5}
									shape={<Rectangle radius={[0, 0, 0, 0]} />}
								/>
							</BarChart>
						</ChartContainer>
					)
				}
			</CardContent>
		</Card>
	);
};
